import { editorInteractionStateTeamUuidAtom } from '@/app/atoms/editorInteractionStateAtom';
import { sheets } from '@/app/grid/controller/Sheets';
import { supportedFileTypesFromGrid } from '@/app/helpers/files';
import { uploadImageToCell } from '@/app/helpers/imageUpload';
import { useFileImport } from '@/app/ui/hooks/useFileImport';
import { useRef } from 'react';
import { useAtomValue } from 'jotai';

export const FILE_INPUT_ID = 'global-file-input-element';
export const IMAGE_INPUT_ID = 'global-image-input-element';

const SUPPORTED_IMAGE_TYPES = ['image/png', 'image/jpeg', 'image/gif', 'image/webp'];

// Maximum dimension (width or height) for newly inserted images in pixels
const MAX_IMAGE_DIMENSION = 600;

/**
 * Calculates the number of columns and rows an image should span based on its pixel dimensions.
 * Uses sheet offsets to determine cell sizes. Images are scaled down to fit within MAX_IMAGE_DIMENSION.
 */
export function calculateImageCellSize(
  startColumn: number,
  startRow: number,
  imageWidth: number,
  imageHeight: number
): { width: number; height: number } {
  // Scale down if either dimension exceeds the maximum
  const scale = Math.min(1, MAX_IMAGE_DIMENSION / Math.max(imageWidth, imageHeight));
  const scaledWidth = imageWidth * scale;
  const scaledHeight = imageHeight * scale;
  const offsets = sheets.sheet.offsets;

  // Calculate columns needed. If a column has zero/negative size (hidden column),
  // we stop early and use the current width rather than spanning hidden columns.
  let width = 1;
  let accumulatedWidth = 0;
  let col = startColumn;
  while (accumulatedWidth < scaledWidth) {
    const placement = offsets.getColumnPlacement(col);
    if (placement.size <= 0) break;
    accumulatedWidth += placement.size;
    if (accumulatedWidth < scaledWidth) {
      width++;
      col++;
    }
  }

  // Calculate rows needed. Same logic as columns for hidden rows.
  let height = 1;
  let accumulatedHeight = 0;
  let row = startRow;
  while (accumulatedHeight < scaledHeight) {
    const placement = offsets.getRowPlacement(row);
    if (placement.size <= 0) break;
    accumulatedHeight += placement.size;
    if (accumulatedHeight < scaledHeight) {
      height++;
      row++;
    }
  }

  return { width, height };
}

/**
 * This component is used to handle file uploads for the grid.
 * It is hidden and only used to trigger the system file picker.
 * The picker is triggered by `insertActionsSpec[Action.InsertFile].run()`
 */
export function GridFileInput() {
  const handleFileImport = useFileImport();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const imageInputRef = useRef<HTMLInputElement>(null);
  const teamUuid = useAtomValue(editorInteractionStateTeamUuidAtom);

  const handleImageUpload = (file: File) => {
    const cursor = sheets.sheet.cursor.position;
    const sheetId = sheets.current;
    uploadImageToCell(file, sheetId, cursor.x, cursor.y);
  };

  return (
    <>
      <input
        id={FILE_INPUT_ID}
        ref={fileInputRef}
        type="file"
        hidden
        accept={supportedFileTypesFromGrid.join(',')}
        onChange={(e) => {
          const files = e.target.files;

          if (files) {
            handleFileImport({
              files: Array.from(files),
              sheetId: sheets.current,
              insertAt: { x: sheets.sheet.cursor.position.x, y: sheets.sheet.cursor.position.y },
              cursor: sheets.getCursorPosition(),
              teamUuid,
            });
          }
        }}
      />
      <input
        id={IMAGE_INPUT_ID}
        ref={imageInputRef}
        type="file"
        hidden
        accept={SUPPORTED_IMAGE_TYPES.join(',')}
        onChange={(e) => {
          const files = e.target.files;
          if (files && files.length > 0) {
            handleImageUpload(files[0]);
          }
        }}
      />
    </>
  );
}
